import { useMemo } from 'react';

import _ from 'lodash';
import { useFormContext } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';

import { ManualCompany } from './ManualCompany';
import { isTestEnv } from '../../../../devOpsHelper/DevOps';
import { SelectWithSearch } from '../../../../forms/selectWithSearch';
import type { RootState } from '../../../../iwProvider/configureStore';
import {
    REQUEST_COMPANY_LOOKUP,
    TOGGLE_MANUAL_COMPANY_STATE
} from '../CompanyDetails.actions';
import type { CompanyDetailsFormValues } from '../CompanyDetails.types';

type Company = { name: string; number: string };

type StateProps = {
    companies?: Company[];
    showManualCompany: boolean;
    fetchCompaniesPending: boolean;
};

type DispatchProps = {
    fetchCompanies: (company: string) => void;
    toggleManualCompanyState: () => void;
};

type Props = StateProps & DispatchProps;

const CompanyNameQuestionView = ({
    companies,
    showManualCompany,
    fetchCompaniesPending,
    fetchCompanies,
    toggleManualCompanyState
}: Props) => {
    const { setValue, watch } = useFormContext<CompanyDetailsFormValues>();

    const companyName = watch('companyName');
    const companyNumber = watch('companyNumber');

    const debouncedFetch = useMemo(
        () => (isTestEnv() ? fetchCompanies : _.debounce(fetchCompanies, 500)),
        [fetchCompanies]
    );

    const items = (companies ?? []).map((company) => ({
        label: `${company.name} (${company.number})`,
        value: company.number
    }));

    const onSelect = (item: { value: string } | null) => {
        if (!item) return;
        const selected = (companies ?? []).find((c) => c.number === item.value);
        if (!selected) return;
        setValue('companyName', selected.name, { shouldDirty: true });
        setValue('companyNumber', selected.number, { shouldDirty: true });
    };

    const onSearchChange = (value: string) => {
        setValue('companyName', value, { shouldDirty: true });
        debouncedFetch(value);
    };

    if (showManualCompany) {
        return (
            <div className="relative mb-xl" data-testid="companyName">
                <ManualCompany
                    companyName={companyName ?? ''}
                    companyNumber={companyNumber ?? ''}
                    onCompanyNameChange={(value) =>
                        setValue('companyName', value, { shouldDirty: true })
                    }
                    onCompanyNumberChange={(value) =>
                        setValue('companyNumber', value, { shouldDirty: true })
                    }
                />
            </div>
        );
    }

    return (
        <div className="relative mb-xl" data-testid="companyName">
            <label>
                <FormattedMessage
                    id="_.auto_company.registered_company_name"
                    defaultMessage="Registered name"
                />
            </label>
            <SelectWithSearch
                value={companyName ?? ''}
                onChange={onSearchChange}
                onButtonClick={onSearchChange}
                items={items}
                isSuccess={Boolean(companies)}
                isLoading={fetchCompaniesPending}
                onSelect={onSelect}
                selectedLabel={companyName ?? ''}
                manualSwitch={toggleManualCompanyState}
                manualSwitchText={
                    <FormattedMessage
                        id="_.auto_company.choose_company_link_text"
                        defaultMessage="enter manually)"
                    />
                }
                helperText={
                    <FormattedMessage
                        id="_.auto_company.choose_company_helper_text"
                        defaultMessage="Choose your company (or"
                    />
                }
                noResultsRenderer={() => (
                    <div>
                        <FormattedMessage
                            id="_.auto_company.company_not_found_error_title"
                            defaultMessage="Oops. We couldn't find your company"
                        />
                    </div>
                )}
                noSearchTermRenderer={() => (
                    <div>
                        <FormattedMessage
                            id="_.auto_company.search_for_company"
                            defaultMessage="Search for your company"
                        />
                    </div>
                )}
                switchPosition="top"
                placeholder="Search..."
            />
            <div>
                <FormattedMessage
                    id="_.auto_company.cant_find_company"
                    defaultMessage="Can't find the company?"
                />{' '}
                <span
                    data-testid="manualCompanyToggleLink"
                    onClick={toggleManualCompanyState}
                >
                    <FormattedMessage
                        id="_.auto_company.enter_manually"
                        defaultMessage="Enter it manually"
                    />
                </span>
            </div>
        </div>
    );
};

const mapStateToProps = (state: RootState): StateProps => ({
    companies: state.profile.companies,
    showManualCompany: state.profile.showManualCompany,
    fetchCompaniesPending: state.profile.fetchCompaniesPending
});

const mapDispatchToProps = (dispatch: Dispatch): DispatchProps => ({
    fetchCompanies: (company: string) =>
        dispatch({ type: REQUEST_COMPANY_LOOKUP, company }),
    toggleManualCompanyState: () =>
        dispatch({ type: TOGGLE_MANUAL_COMPANY_STATE })
});

export const CompanyNameQuestion = connect(
    mapStateToProps,
    mapDispatchToProps
)(CompanyNameQuestionView);
